import {
  type AcquisitionChannel,
  type AdminCustomerSort,
  acquisitionChannelSchema,
  adminCustomerSortSchema,
  objectIdSchema,
} from "@ternary/contracts"

/**
 * The customer list's filters, as the page's URL holds them. Dates are days (YYYY-MM-DD), as a
 * date input gives them; `customerQuery` turns them into the API's ISO 8601 times. `sort` and
 * `dir` are absent for the default order, newest signup first.
 */
export type CustomerFilters = {
  search?: string
  channel?: AcquisitionChannel
  source?: string
  campaign?: string
  productId?: string
  joined?: "yes" | "no"
  /**
   * Member tracking: in the server, not in it, or all. Only a tracking business offers it, and it
   * defaults to in the server there (`withMemberDefault`); `all` keeps the admin's choice of every
   * customer in the URL.
   */
  inServer?: "yes" | "no" | "all"
  from?: string
  to?: string
  sort?: AdminCustomerSort
  dir?: "asc" | "desc"
}

/** The URL parameters `parseCustomerFilters` reads, so a page can clear them. */
export const FILTER_KEYS = [
  "channel",
  "source",
  "campaign",
  "productId",
  "joined",
  "inServer",
  "from",
  "to",
] as const

/**
 * The filters a list asks for: a tracking business lists the members in its server unless the URL
 * chose otherwise; any other business never sends the in-server filter, so its query is today's.
 */
export function withMemberDefault(filters: CustomerFilters, tracking: boolean): CustomerFilters {
  if (tracking) return { ...filters, inServer: filters.inServer ?? "yes" }
  const { inServer: _inServer, ...rest } = filters
  return rest
}

/** The API refuses longer search, source and campaign values. */
const MAX_LENGTH = 200
const DAY = /^\d{4}-\d{2}-\d{2}$/

/** A day the calendar has, such as "2026-10-21" but not "2026-02-31". */
function isDay(value: string) {
  if (!DAY.test(value)) return false
  const date = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(value)
}

/** An exact-match value the API takes: 1 to 200 characters, without a NUL byte. */
function exact(value: string | null) {
  return value && value.length <= MAX_LENGTH && !value.includes("\0") ? value : undefined
}

function oneOf<T extends string>(options: readonly T[], value: string | null): T | undefined {
  return options.find((option) => option === value)
}

/**
 * The filters in a URL. It drops what the API would refuse: an unknown channel, joined, sort or
 * direction, a malformed product id or date, and a source or campaign over 200 characters or with
 * a NUL byte. The search loses NUL bytes and is cut to 200 characters. From after To are swapped.
 */
export function parseCustomerFilters(params: URLSearchParams): CustomerFilters {
  const filters: CustomerFilters = {}
  const search = params.get("search")?.replaceAll("\0", "").trim().slice(0, MAX_LENGTH).trim()
  if (search) filters.search = search
  const channel = oneOf(acquisitionChannelSchema.options, params.get("channel"))
  if (channel) filters.channel = channel
  const source = exact(params.get("source"))
  if (source) filters.source = source
  const campaign = exact(params.get("campaign"))
  if (campaign) filters.campaign = campaign
  const productId = params.get("productId")
  if (productId && objectIdSchema.safeParse(productId).success) filters.productId = productId
  const joined = oneOf(["yes", "no"], params.get("joined"))
  if (joined) filters.joined = joined
  const inServer = oneOf(["yes", "no", "all"], params.get("inServer"))
  if (inServer) filters.inServer = inServer
  let from = params.get("from") ?? ""
  let to = params.get("to") ?? ""
  if (isDay(from) && isDay(to) && from > to) [from, to] = [to, from]
  if (isDay(from)) filters.from = from
  if (isDay(to)) filters.to = to
  const sort = oneOf(adminCustomerSortSchema.options, params.get("sort"))
  if (sort) filters.sort = sort
  const dir = oneOf(["asc", "desc"], params.get("dir"))
  if (dir) filters.dir = dir
  return filters
}

/** How many FilterBar filters are set; the search and the sort are not FilterBar filters. */
export function activeFilterCount(filters: CustomerFilters): number {
  return FILTER_KEYS.filter((key) => filters[key] !== undefined).length
}

// The times with a four-digit year: past them ISO 8601 writes "+010000-…", which the API refuses.
const FIRST_TIME = Date.parse("0000-01-01T00:00:00.000Z")
const LAST_TIME = Date.parse("9999-12-31T23:59:59.999Z")

/**
 * The start of `day` plus `addDays` in the viewer's time zone, as an ISO 8601 UTC time, held to
 * years 0000 to 9999: the day after 9999-12-31 is sent as its last millisecond.
 */
function dayStart(day: string, addDays = 0) {
  const date = new Date(`${day}T00:00:00`)
  date.setDate(date.getDate() + addDays)
  return new Date(Math.min(Math.max(date.getTime(), FIRST_TIME), LAST_TIME)).toISOString()
}

/**
 * The §3.5 list query. From and To are whole days in the viewer's time zone, the zone the list
 * shows dates in: `from` is the start of the first day and `to` the start of the day after the
 * last, because the API's `to` is exclusive. `cursor` is the previous page's `nextCursor`.
 */
export function customerQuery(
  filters: CustomerFilters,
  cursor: string | null,
  limit = 50,
): URLSearchParams {
  const query = new URLSearchParams()
  for (const key of ["search", "channel", "source", "campaign", "productId", "joined"] as const) {
    const value = filters[key]
    if (value !== undefined) query.set(key, value)
  }
  // All is no filter at all.
  if (filters.inServer === "yes" || filters.inServer === "no")
    query.set("inServer", filters.inServer)
  if (filters.from) query.set("from", dayStart(filters.from))
  if (filters.to) query.set("to", dayStart(filters.to, 1))
  if (filters.sort) query.set("sort", filters.sort)
  if (filters.dir) query.set("dir", filters.dir)
  if (cursor !== null) query.set("cursor", cursor)
  query.set("limit", String(limit))
  return query
}

const RANGE_DATE = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" })

/**
 * The From and To days read back as text, such as "Sep 22 – Oct 21, 2026", "From Sep 22, 2026"
 * or "Through Oct 21, 2026", since a native date input shows the browser's own format. Days are
 * in the viewer's time zone, as the list's dates are. Null when neither is set.
 */
export function formatRange(from: string | undefined, to: string | undefined): string | null {
  const day = (value: string) => new Date(`${value}T00:00:00`)
  if (from && to) return RANGE_DATE.formatRange(day(from), day(to))
  if (from) return `From ${RANGE_DATE.format(day(from))}`
  if (to) return `Through ${RANGE_DATE.format(day(to))}`
  return null
}
